import React from 'react'
import { Badge, Box, Checkbox, Heading, HStack, Text } from '@chakra-ui/react'

import { ContentSectionCard } from 'components/TailwindUIToolkit'

import { StepStatus } from '../types'
import InfoHint from './InfoHint'

type Props = {
  step: number
  title: string
  status?: StepStatus
  isLocked?: boolean
  isCompletionDisabled?: boolean
  isChecked?: boolean
  infoTooltip?: string
  completionLabel?: string
  onCheckedChange?: (checked: boolean) => void
  children: React.ReactNode
}

const statusColorMap: Record<StepStatus | 'complete', string> = {
  idle: 'gray',
  running: 'blue',
  success: 'teal',
  warning: 'yellow',
  error: 'red',
  complete: 'green'
}

export const StepCard = ({
  step,
  title,
  status = 'idle',
  isLocked = false,
  isCompletionDisabled = false,
  isChecked = false,
  infoTooltip,
  completionLabel = 'Mark step as complete',
  onCheckedChange,
  children
}: Props) => {
  const effectiveStatus = isChecked ? 'complete' : status
  const statusLabel = isChecked ? 'Complete' : status

  return (
    <Box opacity={isLocked ? 0.55 : 1}>
      <ContentSectionCard
        title={
          <HStack spacing={1}>
            <Heading as='h3' size='md'>
              {step}. {title}
            </Heading>
            {infoTooltip && <InfoHint label={infoTooltip} />}
          </HStack>
        }
        action={
          <Badge colorScheme={statusColorMap[effectiveStatus]} textTransform='capitalize'>
            {statusLabel}
          </Badge>
        }
        padding
        size='fit-content'
        staticSize
      >
        {isLocked && (
          <Text fontSize='sm' color='gray.600' mb={3}>
            Complete the previous step to unlock this one.
          </Text>
        )}
        <Box pointerEvents={isLocked ? 'none' : 'auto'}>{children}</Box>
        {onCheckedChange && (
          <Checkbox
            mt={4}
            isChecked={isChecked}
            isDisabled={isLocked || isCompletionDisabled}
            onChange={(event) => onCheckedChange(event.target.checked)}
          >
            {completionLabel}
          </Checkbox>
        )}
      </ContentSectionCard>
    </Box>
  )
}

export default StepCard
